// src/components/ClaimForm.js - CLAIM FORM COMPONENT (create + edit)
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Plus, X, Upload, Check, AlertCircle,
  Calendar, DollarSign, FileText, User, ChevronDown, Search, Pencil, Trash2
} from 'lucide-react';


const SearchableSelect = ({ options, value, onChange, placeholder, disabled }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const filtered = options.filter(opt =>
    opt.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (option) => {
    onChange(option);
    setSearch('');
    setOpen(false);
  };

  const handleToggle = () => {
    if (!disabled) setOpen(prev => !prev);
  };

  return (
    <div className={`searchable-select${disabled ? ' searchable-select--disabled' : ''}`} ref={ref}>
      <button
        type="button"
        className="searchable-select__trigger"
        onClick={handleToggle}
        disabled={disabled}
      >
        <span className={value ? '' : 'searchable-select__placeholder'}>
          {value || placeholder}
        </span>
        <ChevronDown size={16} className={`searchable-select__chevron${open ? ' open' : ''}`} />
      </button>
      {open && (
        <div className="searchable-select__dropdown">
          <div className="searchable-select__search">
            <Search size={14} />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <ul className="searchable-select__list">
            {filtered.length > 0 ? filtered.map(opt => (
              <li
                key={opt}
                className={`searchable-select__option${opt === value ? ' active' : ''}`}
                onClick={() => handleSelect(opt)}
              >
                {opt}
              </li>
            )) : (
              <li className="searchable-select__no-results">No matches found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const EDITABLE_STATUSES = ['pending', 'attention_required'];

// Universal tooth numbering system — permanent teeth 1-32, primary
// (deciduous) teeth lettered A-T. A fixed, real dental standard, not
// something CORE has a reference table for — unlike service_category
// (DentalServiceType), there's nothing to sync here.
const TOOTH_NUMBER_CHOICES = [
  ...Array.from({ length: 32 }, (_, i) => String(i + 1)),
  ...'ABCDEFGHIJKLMNOPQRST'.split(''),
];

// Standard tooth surface codes: Mesial, Distal, Occlusal, Buccal, Lingual,
// Incisal.
const TOOTH_SURFACE_CHOICES = ['M', 'D', 'O', 'B', 'L', 'I'];

const emptyLineItem = (user) => ({
  service_date: '',
  description: '',
  cost: '',
  service_category: '',
  sub_service_description: '',
  tooth_number: '',
  tooth_surface: '',
  provider_name: '',
  alternate_coverage_paid: '',
  drug_identification_number: '',
  number_of_units: '',
  dispensing_fee: '',
  dependent: user.dependents?.[0]?.id ?? null,
  is_for_self: false,
  beneficiary_name: user.dependents?.[0]?.name ?? '',
});

const ClaimForm = ({ user }) => {
  const { type: urlType, claimId } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!claimId;

  const [claimType, setClaimType] = useState(urlType || '');
  const [claimStatus, setClaimStatus] = useState(null);
  const [adminResponse, setAdminResponse] = useState('');
  const [fetchingClaim, setFetchingClaim] = useState(isEditMode);

  const [formData, setFormData] = useState({
    claim_type: urlType || '',
    member: user.member_number,
    member_notes: '',
    line_items: [emptyLineItem(user)],
  });

  const [files, setFiles] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState({});
  const [availableServices, setAvailableServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [commonProcedures, setCommonProcedures] = useState([]);
  const [commonProceduresLoading, setCommonProceduresLoading] = useState(false);

  const newItemRef = useRef(null);

  const claimTypeInfo = {
    dental: {
      title: 'Dental Claim',
      description: 'Submit claims for dental procedures, cleanings, and treatments',
      icon: '🦷',
    },
    health: {
      title: 'Extended Health Claim',
      description: 'Submit claims for medical services, prescriptions, and equipment',
      icon: '🏥',
    }
  };

  const currentClaimInfo = claimTypeInfo[claimType] || claimTypeInfo.health;

  // Fetch existing claim in edit mode
  useEffect(() => {
    if (!isEditMode) return;
    const fetchClaim = async () => {
      try {
        setFetchingClaim(true);
        const response = await axios.get(`/api/claims/${claimId}/`);
        const claim = response.data;

        if (!EDITABLE_STATUSES.includes(claim.status)) {
          navigate(`/claim/${claimId}`);
          return;
        }

        setClaimType(claim.claim_type);
        setClaimStatus(claim.status);
        setAdminResponse(claim.admin_response || '');
        setExistingDocuments(claim.documents || []);

        const mappedLineItems = claim.line_items.map(item => ({
          service_date: item.service_date,
          description: item.description,
          cost: String(item.cost),
          service_category: item.service_category || '',
          sub_service_description: item.description || '',
          tooth_number: item.tooth_number || '',
          tooth_surface: item.tooth_surface || '',
          provider_name: item.provider_name || '',
          alternate_coverage_paid: item.alternate_coverage_paid != null ? String(item.alternate_coverage_paid) : '',
          drug_identification_number: item.drug_identification_number || '',
          number_of_units: '',
          dispensing_fee: '',
          dependent: item.dependent || null,
          is_for_self: item.is_for_self,
          beneficiary_name: item.beneficiary_name || '',
        }));
        setFormData({
          claim_type: claim.claim_type,
          member: user.member_number,
          member_notes: claim.member_notes || '',
          line_items: mappedLineItems,
        });
      } catch (err) {
        setError('Failed to load claim. Please try again.');
      } finally {
        setFetchingClaim(false);
      }
    };
    fetchClaim();
  }, [claimId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch available plan services (health) / dental procedure categories
  useEffect(() => {
    const fetchAvailableServices = async () => {
      setServicesLoading(true);
      try {
        if (claimType === 'health' && user.plan_code) {
          const response = await axios.get(`/api/plans/${user.plan_code}/services/`);
          if (response.data.services) setAvailableServices(response.data.services);
        } else if (claimType === 'dental') {
          // Not plan-gated like health's services — DentalServiceType is a
          // fixed, universal category list, so this ignores user.plan_code.
          const response = await axios.get('/api/services/?claim_type=dental');
          const results = response.data.results || response.data;
          setAvailableServices(results.map((s) => s.name));
        }
      } catch (err) {
        setAvailableServices([]);
      } finally {
        setServicesLoading(false);
      }
    };
    fetchAvailableServices();
  }, [claimType, user.plan_code]);

  // Fetch the curated common-procedure quick-fill list (dental only) —
  // separate from availableServices/servicesLoading above since this only
  // ever powers an optional quick-fill for the free-text description, not
  // a required field of its own.
  useEffect(() => {
    if (claimType !== 'dental') {
      setCommonProcedures([]);
      return;
    }
    const fetchCommonProcedures = async () => {
      setCommonProceduresLoading(true);
      try {
        const response = await axios.get('/api/services/?claim_type=dentalproc');
        const results = response.data.results || response.data;
        // `description` carries the matching broad-category label (e.g.
        // 'BASIC') CORE's sync now stashes there -- lets picking a specific
        // procedure also auto-select the right category below.
        setCommonProcedures(results.map((s) => ({ name: s.name, category: s.description || '' })));
      } catch (err) {
        setCommonProcedures([]);
      } finally {
        setCommonProceduresLoading(false);
      }
    };
    fetchCommonProcedures();
  }, [claimType]);

  const handleInputChange = (e) => {
    const { name, value, type: inputType, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: inputType === 'checkbox' ? checked : value,
    }));
  };

  const handleLineItemChange = (index, field, value) => {
    const newLineItems = [...formData.line_items];
    newLineItems[index] = { ...newLineItems[index], [field]: value };
    setFormData({ ...formData, line_items: newLineItems });
  };

  // Like handleLineItemChange, but for setting more than one field on the
  // same line item at once (e.g. the procedure quick-fill setting both the
  // description and the category together). Needed because
  // handleLineItemChange closes over `formData` directly rather than using
  // a functional setFormData(prev => ...) update -- calling it twice in a
  // row in the same handler would silently drop the first change.
  const handleLineItemFieldsChange = (index, fields) => {
    const newLineItems = [...formData.line_items];
    newLineItems[index] = { ...newLineItems[index], ...fields };
    setFormData({ ...formData, line_items: newLineItems });
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      line_items: [...formData.line_items, emptyLineItem(user)],
    });
    setTimeout(() => {
      newItemRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const removeLineItem = (index) => {
    if (formData.line_items.length > 1) {
      setFormData({
        ...formData,
        line_items: formData.line_items.filter((_, i) => i !== index),
      });
    }
  };

  const handleFileChange = (e) => {
    setFiles(prev => [...prev, ...Array.from(e.target.files)]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingDocument = async (docId) => {
    try {
      await axios.delete(`/api/claims/${claimId}/delete_document/${docId}/`);
      setExistingDocuments(prev => prev.filter(d => d.id !== docId));
    } catch (err) {
      setError('Failed to remove document. Please try again.');
    }
  };

  const calculateTotal = () =>
    formData.line_items.reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0);

  const validateForm = () => {
    for (let item of formData.line_items) {
      if (!item.service_date || !item.cost) return 'Please fill in service date and cost for all items.';
      if (parseFloat(item.cost) <= 0) return 'All service costs must be greater than zero.';
      // SearchableSelect (used for both health's service dropdown and dental's
      // procedure-category dropdown) has no native HTML "required" enforcement
      // like the old dental free-text <input> did, so this has to catch it here.
      if (availableServices.length > 0 && !item.service_category)
        return claimType === 'dental'
          ? 'Please select a procedure category for all items.'
          : 'Please select a service category for all items.';
      if (!item.description && !item.sub_service_description && !item.service_category)
        return 'Please provide a service description for all items.';
      if (!item.is_for_self && (!item.beneficiary_name || item.beneficiary_name.trim() === ''))
        return "Please enter the patient's full name when \"Other\" is selected.";
      if (claimType === 'health' && item.alternate_coverage_paid !== '' && parseFloat(item.alternate_coverage_paid) < 0)
        return 'Alternate Coverage Paid must be zero or greater.';
      if (claimType === 'health' && item.service_category === 'DRUGS') {
        if (!item.drug_identification_number?.trim()) return 'Drug Identification Number (DIN) is required for drug claims.';
        if (!item.number_of_units || parseFloat(item.number_of_units) <= 0) return 'Number of units is required and must be greater than zero for drug claims.';
        if (item.dispensing_fee === '' || item.dispensing_fee === null || item.dispensing_fee === undefined) return 'Dispensing fee is required for drug claims.';
        if (parseFloat(item.dispensing_fee) < 0) return 'Dispensing fee cannot be negative.';
      }
    }
    return null;
  };

  const buildLineItemsForSubmit = () =>
    formData.line_items.map(item => {
      const lineItem = {
        ...item,
        // service_category saved as its own field
        service_category: item.service_category || '',
        // description is the specific description; falls back to category if blank
        description: item.sub_service_description || item.description || item.service_category || 'Service',
      };
      if (lineItem.alternate_coverage_paid === '' || lineItem.alternate_coverage_paid === null || lineItem.alternate_coverage_paid === undefined)
        lineItem.alternate_coverage_paid = 0;
      if (claimType === 'dental') {
        delete lineItem.alternate_coverage_paid;
        delete lineItem.drug_identification_number;
        delete lineItem.number_of_units;
        delete lineItem.dispensing_fee;
      }
      return lineItem;
    });

  const uploadFiles = async (targetClaimId) => {
    await Promise.allSettled(files.map(async (file, index) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', 'receipt');
      fd.append('description', `Receipt ${index + 1}`);
      try {
        setUploadProgress(prev => ({ ...prev, [index]: 0 }));
        await axios.post(`/api/claims/${targetClaimId}/upload_document/`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            setUploadProgress(prev => ({ ...prev, [index]: Math.round((e.loaded * 100) / e.total) }));
          },
        });
      } catch (err) {
        console.error(`Failed to upload ${file.name}:`, err);
      }
    }));
  };

  const submitClaim = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.post('/api/claims/', {
        ...formData,
        line_items: buildLineItemsForSubmit(),
      });
      if (response.data.success) {
        const newId = response.data.claim.id;
        if (files.length > 0) await uploadFiles(newId);
        navigate('/dashboard', { state: { message: `Claim #${newId} submitted successfully!`, type: 'success' } });
      } else {
        throw new Error(response.data.message || 'Failed to submit claim');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.errors || err.message || 'Failed to submit claim. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateClaim = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.patch(`/api/claims/${claimId}/update_claim/`, {
        member_notes: formData.member_notes,
        line_items: buildLineItemsForSubmit(),
      });
      if (response.data.success) {
        if (files.length > 0) await uploadFiles(claimId);
        navigate(`/claim/${claimId}`, { state: { message: response.data.message, type: 'success' } });
      } else {
        throw new Error(response.data.message || 'Failed to update claim');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.errors || err.message || 'Failed to update claim. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) { setError(validationError); return; }
    if (isEditMode) updateClaim(); else submitClaim();
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount || 0);

  if (fetchingClaim) {
    return (
      <div className="claim-form-container">
        <div className="loading-detail">
          <div className="loading-spinner"></div>
          <p>Loading claim...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="claim-form-container">
      <header className="claim-form-header">
        <button
          onClick={() => navigate('/dashboard')}
          className="back-button"
          disabled={loading}
        >
          <ArrowLeft size={20} />
          <span>Back to Dashboard</span>
        </button>

        <div className="header-info">
          <span className="claim-icon">{isEditMode ? '✏️' : currentClaimInfo.icon}</span>
          <div>
            <h1>{isEditMode ? `Edit Claim #${claimId}` : currentClaimInfo.title}</h1>
            <p>
              {isEditMode
                ? `${currentClaimInfo.title} — update services or notes below`
                : currentClaimInfo.description}
            </p>
          </div>
        </div>
      </header>

      <form className="claim-form" onSubmit={handleSubmit}>

        {/* Attention required notice */}
        {isEditMode && claimStatus === 'attention_required' && (
          <div className="attention-banner" style={{ margin: '0 0 8px' }}>
            <AlertCircle size={22} style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <strong>Action Required — Response from CoRe Group Benefits</strong>
              {adminResponse && (
                <p className="edit-admin-response-text">{adminResponse}</p>
              )}
              <p style={{ marginTop: adminResponse ? 10 : 0, fontSize: '0.8rem', opacity: 0.85 }}>
                Update your claim below and save to resubmit for review.
              </p>
            </div>
          </div>
        )}

        {/* Member Information */}
        <div className="form-section">
          <div className="section-header">
            <User className="section-icon" />
            <h2>Member Information</h2>
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label>Member Number</label>
              <input type="text" value={user.member_number} disabled className="disabled-field" />
            </div>
            <div className="form-group">
              <label>Plan Code</label>
              <input type="text" value={user.plan_code} disabled className="disabled-field" />
            </div>
            <div className="form-group">
              <label>Member Name</label>
              <input type="text" value={user.full_name} disabled className="disabled-field" />
            </div>
          </div>
        </div>

        {/* Service Details */}
        <div className="form-section">
          <div className="section-header">
            <FileText className="section-icon" />
            <h2>Service Details</h2>
          </div>

          {user.plan_code && (
            <div className="plan-info">
              <p><strong>Your Plan:</strong> {user.plan_code}</p>
              {availableServices.length > 0 && (
                <p><small>{availableServices.length} services covered by your plan</small></p>
              )}
            </div>
          )}

          <div className="claim-tip-banner">
            <AlertCircle size={16} />
            <span>
              <strong>Tip:</strong> Add all services from the same visit or time period to this single claim — one claim per visit keeps things organised and speeds up processing.
            </span>
          </div>

          {formData.line_items.map((item, index) => (
            <div
              key={index}
              className="line-item"
              ref={index === formData.line_items.length - 1 ? newItemRef : null}
            >
              <div className="line-item-header">
                <h3>Service {index + 1}</h3>
                {formData.line_items.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeLineItem(index)}
                    className="remove-button"
                    disabled={loading}
                  >
                    <X size={18} />
                    <span>Remove</span>
                  </button>
                )}
              </div>

              {/* Beneficiary Selection */}
              <div className="form-section beneficiary-section">
                <div className="form-group beneficiary-group">
                  <label htmlFor={`beneficiary-select-${index}`}>
                    <User size={16} />
                    This service is for:
                  </label>
                  <select
                    id={`beneficiary-select-${index}`}
                    className="beneficiary-select"
                    disabled={loading}
                    value={item.dependent !== null ? `dep-${item.dependent}` : 'other'}
                    onChange={(e) => {
                      const val = e.target.value;
                      const newLineItems = [...formData.line_items];
                      if (val === 'other') {
                        newLineItems[index] = { ...newLineItems[index], is_for_self: false, dependent: null, beneficiary_name: '' };
                      } else {
                        const dep = (user.dependents || []).find(d => `dep-${d.id}` === val);
                        if (dep) newLineItems[index] = { ...newLineItems[index], is_for_self: false, dependent: dep.id, beneficiary_name: dep.name };
                      }
                      setFormData({ ...formData, line_items: newLineItems });
                    }}
                  >
                    {(user.dependents || []).filter(d => d.is_active).map(dep => (
                      <option key={dep.id} value={`dep-${dep.id}`}>
                        {dep.name} ({dep.relationship_display})
                      </option>
                    ))}
                    <option value="other">Other</option>
                  </select>

                  {!item.is_for_self && item.dependent === null && (
                    <div className="custom-name-input">
                      <input
                        type="text"
                        value={item.beneficiary_name || ''}
                        onChange={(e) => handleLineItemChange(index, 'beneficiary_name', e.target.value)}
                        placeholder="Enter patient's full name"
                        required
                        disabled={loading}
                        className="beneficiary-name-input"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label><Calendar size={16} /> Service Date</label>
                  <input
                    type="date"
                    value={item.service_date}
                    onChange={(e) => handleLineItemChange(index, 'service_date', e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label><DollarSign size={16} /> Cost (CAD)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={item.cost}
                    onChange={(e) => handleLineItemChange(index, 'cost', e.target.value)}
                    placeholder="0.00"
                    required
                    disabled={loading}
                  />
                </div>

                {claimType === 'dental' && (
                  <details className="tooth-details-toggle">
                    <summary>Tooth number / surface (if applicable)</summary>
                    <div className="form-group">
                      <label>Tooth Number</label>
                      <SearchableSelect
                        options={TOOTH_NUMBER_CHOICES}
                        value={item.tooth_number}
                        onChange={(val) => handleLineItemChange(index, 'tooth_number', val)}
                        placeholder="Select tooth number (if applicable)..."
                        disabled={loading}
                      />
                    </div>
                    <div className="form-group">
                      <label>Tooth Surface</label>
                      <SearchableSelect
                        options={TOOTH_SURFACE_CHOICES}
                        value={item.tooth_surface}
                        onChange={(val) => handleLineItemChange(index, 'tooth_surface', val)}
                        placeholder="Select tooth surface (if applicable)..."
                        disabled={loading}
                      />
                    </div>
                  </details>
                )}

                {claimType === 'health' && (
                  <>
                    <div className="form-group">
                      <label>Provider Name</label>
                      <input
                        type="text"
                        value={item.provider_name}
                        onChange={(e) => handleLineItemChange(index, 'provider_name', e.target.value)}
                        placeholder="Doctor, clinic, or pharmacy name"
                        disabled={loading}
                      />
                    </div>
                    <div className="form-group">
                      <label><DollarSign size={16} /> Alternate Coverage Paid (CAD)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={item.alternate_coverage_paid}
                        onChange={(e) => handleLineItemChange(index, 'alternate_coverage_paid', e.target.value)}
                        placeholder="0.00 (leave blank if none)"
                        disabled={loading}
                      />
                    </div>
                  </>
                )}

                {claimType === 'health' && item.service_category === 'DRUGS' && (
                  <>
                    <div className="form-group">
                      <label>Drug Identification Number (DIN) *</label>
                      <input
                        type="text"
                        value={item.drug_identification_number}
                        onChange={(e) => handleLineItemChange(index, 'drug_identification_number', e.target.value)}
                        placeholder="e.g., 02247113"
                        required
                        disabled={loading}
                      />
                    </div>
                    <div className="form-group">
                      <label>Number of Units *</label>
                      <input
                        type="number"
                        min="1"
                        value={item.number_of_units}
                        onChange={(e) => handleLineItemChange(index, 'number_of_units', e.target.value)}
                        placeholder="e.g., 30"
                        required
                        disabled={loading}
                      />
                    </div>
                    <div className="form-group">
                      <label><DollarSign size={16} /> Dispensing Fee (CAD) *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={item.dispensing_fee}
                        onChange={(e) => handleLineItemChange(index, 'dispensing_fee', e.target.value)}
                        placeholder="0.00"
                        required
                        disabled={loading}
                      />
                    </div>
                  </>
                )}

                <div className="form-group">
                  <label>{claimType === 'dental' ? 'Procedure / Service' : 'Service Category'}</label>
                  {isEditMode && !item.service_category && (
                    <div className="field-reselect-notice">
                      <AlertCircle size={14} />
                      <span>Could not recover original value — please re-enter.</span>
                    </div>
                  )}
                  {servicesLoading ? (
                    <div className="service-loading">Loading available {claimType === 'dental' ? 'categories' : 'services'}...</div>
                  ) : (
                    <SearchableSelect
                      options={availableServices}
                      value={item.service_category}
                      onChange={(val) => handleLineItemChange(index, 'service_category', val)}
                      placeholder={claimType === 'dental' ? 'Select a procedure category...' : 'Select a service covered by your plan...'}
                      disabled={loading}
                    />
                  )}
                </div>

                <div className="form-group full-width">
                  <label>Specific Service Description</label>
                  {claimType === 'dental' && commonProceduresLoading && (
                    <div className="service-loading">Loading common procedures...</div>
                  )}
                  {claimType === 'dental' && !commonProceduresLoading && commonProcedures.length > 0 && (
                    <SearchableSelect
                      options={commonProcedures.map((p) => p.name)}
                      value=""
                      onChange={(val) => {
                        const matched = commonProcedures.find((p) => p.name === val);
                        handleLineItemFieldsChange(index, {
                          sub_service_description: val,
                          // Also set the broad category to match, if we have
                          // one -- still just a default, not locked: the
                          // category dropdown above stays fully editable.
                          ...(matched?.category ? { service_category: matched.category } : {}),
                        });
                      }}
                      placeholder="Quick-fill from a common procedure (optional)..."
                      disabled={loading}
                    />
                  )}
                  <input
                    type="text"
                    value={item.sub_service_description || item.description}
                    onChange={(e) => handleLineItemChange(index, 'sub_service_description', e.target.value)}
                    placeholder="e.g., Amoxicillin 500mg, Regular cleaning, MRI scan"
                    required
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={addLineItem}
            className="add-service-button"
            disabled={loading}
          >
            <Plus size={20} />
            <span>Add Another Service to This Claim</span>
          </button>
        </div>

        {/* Existing Documents (edit mode only) */}
        {isEditMode && existingDocuments.length > 0 && (
          <div className="form-section">
            <div className="section-header">
              <FileText className="section-icon" />
              <h2>Attached Documents ({existingDocuments.length})</h2>
            </div>
            <div className="existing-documents-list">
              {existingDocuments.map(doc => (
                <div key={doc.id} className="existing-document-item">
                  <div className="document-info">
                    <FileText size={18} className="document-icon" />
                    <div className="document-details">
                      <span className="document-name">{doc.original_filename}</span>
                      <span className="document-meta">{doc.file_size_human}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeExistingDocument(doc.id)}
                    className="remove-document-button"
                    disabled={loading}
                    title="Remove document"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upload new files */}
        <div className="form-section">
          <div className="section-header">
            <Upload className="section-icon" />
            <h2>{isEditMode ? 'Add More Documents' : 'Upload Receipts'}</h2>
          </div>

          <div className="file-upload-area">
            <input
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx"
              onChange={handleFileChange}
              className="file-input"
              id="file-upload"
              disabled={loading}
            />
            <label htmlFor="file-upload" className="file-upload-label">
              <Upload size={32} className="upload-icon" />
              <div className="upload-text">
                <h4>Upload receipts and supporting documents</h4>
                <p>Drop files here or click to browse</p>
                <small>Supported: Images, PDFs, Word documents</small>
              </div>
            </label>
          </div>

          {files.length > 0 && (
            <div className="uploaded-files">
              <h4>Selected Files ({files.length}):</h4>
              <div className="files-list">
                {files.map((file, index) => (
                  <div key={index} className="file-item">
                    <div className="file-info">
                      <FileText size={16} />
                      <span className="file-name">{file.name}</span>
                      <span className="file-size">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="remove-file-button"
                      disabled={loading}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Member Notes */}
        <div className="form-section">
          <div className="section-header">
            <FileText className="section-icon" />
            <h2>Additional Information</h2>
          </div>
          <div className="form-group">
            <label>Notes (Optional)</label>
            <textarea
              name="member_notes"
              value={formData.member_notes}
              onChange={handleInputChange}
              placeholder="Any additional information about this claim..."
              rows={3}
              disabled={loading}
            />
          </div>
        </div>

        {/* Summary */}
        <div className="form-section summary-section">
          <div className="section-header">
            <DollarSign className="section-icon" />
            <h2>Claim Summary</h2>
          </div>
          <div className="summary-content">
            <div className="summary-item">
              <span>Services:</span>
              <span>{formData.line_items.length} items</span>
            </div>
            {isEditMode && existingDocuments.length > 0 && (
              <div className="summary-item">
                <span>Existing Documents:</span>
                <span>{existingDocuments.length} files</span>
              </div>
            )}
            <div className="summary-item">
              <span>New Documents:</span>
              <span>{files.length} files</span>
            </div>
            <div className="summary-item total">
              <span>Total Amount:</span>
              <span className="total-amount">{formatCurrency(calculateTotal())}</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <AlertCircle size={20} />
            <div className="error-content">
              <strong>{isEditMode ? 'Update Error' : 'Submission Error'}</strong>
              <p>{error}</p>
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="cancel-button"
            disabled={loading}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="submit-button"
            disabled={loading || calculateTotal() === 0}
          >
            {loading ? (
              <>
                <div className="loading-spinner"></div>
                <span>{isEditMode ? 'Saving...' : 'Submitting...'}</span>
              </>
            ) : isEditMode ? (
              <>
                <Pencil size={18} />
                <span>Save Changes</span>
              </>
            ) : (
              <>
                <Check size={18} />
                <span>Submit Claim</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ClaimForm;
